import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import { publicApi } from '../../utils/publicApi';
import QRCode from 'react-qr-code';
import { FaUser, FaTrash, FaCamera, FaEnvelope, FaIdCard, FaPlane, FaHotel, FaFileSignature, FaCar, FaPencilAlt, FaShieldAlt, FaCopy } from 'react-icons/fa';
import ProfileModal from '../../components/ProfileModal';
import { PROFILE_COMPANY_CAPITAL_OPTIONS, PROFILE_SENIORITY_OPTIONS } from '../../data/profileFieldOptions';

const COUNTRY_NATIONALITY_OPTIONS = [
  { country: 'Oman', nationality: 'Omani' },
  { country: 'United Arab Emirates', nationality: 'Emirati' },
  { country: 'Saudi Arabia', nationality: 'Saudi' },
  { country: 'Qatar', nationality: 'Qatari' },
  { country: 'Kuwait', nationality: 'Kuwaiti' },
  { country: 'Bahrain', nationality: 'Bahraini' },
  { country: 'Yemen', nationality: 'Yemeni' },
  { country: 'Jordan', nationality: 'Jordanian' },
  { country: 'Lebanon', nationality: 'Lebanese' },
  { country: 'Syria', nationality: 'Syrian' },
  { country: 'Iraq', nationality: 'Iraqi' },
  { country: 'Egypt', nationality: 'Egyptian' },
  { country: 'Morocco', nationality: 'Moroccan' },
  { country: 'Algeria', nationality: 'Algerian' },
  { country: 'Tunisia', nationality: 'Tunisian' },
  { country: 'Libya', nationality: 'Libyan' },
  { country: 'Sudan', nationality: 'Sudanese' },
  { country: 'United States', nationality: 'American' },
  { country: 'Canada', nationality: 'Canadian' },
  { country: 'Mexico', nationality: 'Mexican' },
  { country: 'Brazil', nationality: 'Brazilian' },
  { country: 'Argentina', nationality: 'Argentinian' },
  { country: 'Chile', nationality: 'Chilean' },
  { country: 'Peru', nationality: 'Peruvian' },
  { country: 'United Kingdom', nationality: 'British' },
  { country: 'Ireland', nationality: 'Irish' },
  { country: 'France', nationality: 'French' },
  { country: 'Germany', nationality: 'German' },
  { country: 'Italy', nationality: 'Italian' },
  { country: 'Spain', nationality: 'Spanish' },
  { country: 'Portugal', nationality: 'Portuguese' },
  { country: 'Netherlands', nationality: 'Dutch' },
  { country: 'Belgium', nationality: 'Belgian' },
  { country: 'Switzerland', nationality: 'Swiss' },
  { country: 'Austria', nationality: 'Austrian' },
  { country: 'Sweden', nationality: 'Swedish' },
  { country: 'Norway', nationality: 'Norwegian' },
  { country: 'Denmark', nationality: 'Danish' },
  { country: 'Finland', nationality: 'Finnish' },
  { country: 'Poland', nationality: 'Polish' },
  { country: 'Russia', nationality: 'Russian' },
  { country: 'Ukraine', nationality: 'Ukrainian' },
  { country: 'Turkey', nationality: 'Turkish' },
  { country: 'Iran', nationality: 'Iranian' },
  { country: 'Afghanistan', nationality: 'Afghan' },
  { country: 'Pakistan', nationality: 'Pakistani' },
  { country: 'India', nationality: 'Indian' },
  { country: 'Bangladesh', nationality: 'Bangladeshi' },
  { country: 'Sri Lanka', nationality: 'Sri Lankan' },
  { country: 'Nepal', nationality: 'Nepali' },
  { country: 'China', nationality: 'Chinese' },
  { country: 'Japan', nationality: 'Japanese' },
  { country: 'South Korea', nationality: 'Korean' },
  { country: 'Thailand', nationality: 'Thai' },
  { country: 'Vietnam', nationality: 'Vietnamese' },
  { country: 'Philippines', nationality: 'Filipino' },
  { country: 'Indonesia', nationality: 'Indonesian' },
  { country: 'Malaysia', nationality: 'Malaysian' },
  { country: 'Singapore', nationality: 'Singaporean' },
  { country: 'Australia', nationality: 'Australian' },
  { country: 'New Zealand', nationality: 'New Zealander' },
  { country: 'South Africa', nationality: 'South African' },
  { country: 'Nigeria', nationality: 'Nigerian' },
  { country: 'Kenya', nationality: 'Kenyan' },
  { country: 'Ethiopia', nationality: 'Ethiopian' },
  { country: 'Ghana', nationality: 'Ghanaian' },
];

const COUNTRY_OPTIONS = COUNTRY_NATIONALITY_OPTIONS.map((item) => item.country);
const NATIONALITY_OPTIONS = COUNTRY_NATIONALITY_OPTIONS.map((item) => item.nationality);

const COUNTRY_CODE_OPTIONS = [
  { label: 'Oman (+968)', value: '+968' },
  { label: 'UAE (+971)', value: '+971' },
  { label: 'Saudi Arabia (+966)', value: '+966' },
  { label: 'Qatar (+974)', value: '+974' },
  { label: 'Kuwait (+965)', value: '+965' },
  { label: 'Bahrain (+973)', value: '+973' },
  { label: 'India (+91)', value: '+91' },
  { label: 'Pakistan (+92)', value: '+92' },
  { label: 'United Kingdom (+44)', value: '+44' },
  { label: 'United States (+1)', value: '+1' },
];

const stripCountryCodeFromNumber = (rawNumber, countryCode) => {
  const codeDigits = String(countryCode || '').replace(/\D/g, '');
  let digits = String(rawNumber || '').replace(/\D/g, '');
  if (codeDigits && digits.startsWith(codeDigits)) {
    digits = digits.slice(codeDigits.length);
  }
  return digits.slice(0, 10);
};

const splitSavedPhone = (savedPhone) => {
  const normalized = String(savedPhone || '').trim();
  if (!normalized.startsWith('+')) {
    return { code: '+968', number: normalized.replace(/\D/g, '').slice(0, 10) };
  }
  const sortedCodes = [...COUNTRY_CODE_OPTIONS]
    .map((item) => item.value)
    .sort((a, b) => b.length - a.length);
  const matchedCode = sortedCodes.find((code) => normalized.startsWith(code)) || '+968';
  const restNumber = normalized.slice(matchedCode.length).replace(/\D/g, '').slice(0, 10);
  return { code: matchedCode, number: restNumber };
};

const toYesNoOrEmpty = (value) => {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return '';
};

const parseHotelInfo = (hotelAssignedRaw) => {
  const hotelAssigned = String(hotelAssignedRaw || '');
  if (!hotelAssigned) {
    return { hotel_assigned: '', hotel_name: '', check_in: '', check_out: '' };
  }
  if (hotelAssigned === 'NO') {
    return { hotel_assigned: 'no', hotel_name: '', check_in: '', check_out: '' };
  }
  if (hotelAssigned.startsWith('YES')) {
    const [, name = '', checkIn = '', checkOut = ''] = hotelAssigned.split('|');
    return {
      hotel_assigned: 'yes',
      hotel_name: name,
      check_in: checkIn ? checkIn.split('T')[0] : '',
      check_out: checkOut ? checkOut.split('T')[0] : '',
    };
  }
  return { hotel_assigned: '', hotel_name: '', check_in: '', check_out: '' };
};

const parseVisaInfo = (visaInfoRaw) => {
  const visaInfo = String(visaInfoRaw || '');
  if (!visaInfo) return { full_name: '', passport_number: '' };
  const [fullName = '', passportNumber = ''] = visaInfo.split('|');
  return { full_name: fullName, passport_number: passportNumber };
};

const DetailRow = ({ label, value, className = '' }) => (
  <div className={`flex justify-between gap-2 ${className}`.trim()}>
    <span className="text-slate-500 shrink-0">{label}</span>
    <span className="text-slate-900 font-medium text-right truncate">{value || '—'}</span>
  </div>
);

export const ProfileContent = () => {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const { logout, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingAvatar, setChangingAvatar] = useState(false);
  const [avatarTimestamp, setAvatarTimestamp] = useState(Date.now());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Account Settings Form (no driver – admin fills driver)
  const [formData, setFormData] = useState({
    full_name: '',
    org: '',
    company_capital_size: '',
    position: '',
    seniority_level: '',
    nationality: '',
    country: '',
    phone_country_code: '+968',
    phone_number: '',
    sector: '',
  });

  // Security Form
  const [securityData, setSecurityData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [openModal, setOpenModal] = useState(null);
  const [travelFormData, setTravelFormData] = useState({ flight_issued: '', flight_number: '', arrival_date: '', arrival_time: '', departure_date: '', departure_time: '' });
  const [hotelFormData, setHotelFormData] = useState({ hotel_assigned: '', hotel_name: '', check_in: '', check_out: '' });
  const [visaFormData, setVisaFormData] = useState({ visa_letter_required: '', visa_letter_issued: '', full_name: '', passport_number: '' });
  const [deleteUnderstand, setDeleteUnderstand] = useState(false);
  const [copiedId, setCopiedId] = useState(false);

  const fileInputRef = useRef(null);

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const [profileData, travelData] = await Promise.all([
        publicApi.getProfile(),
        publicApi.getTravelDetails(),
      ]);
      if (profileData) {
        const parsedPhone = splitSavedPhone(profileData.phone || '');
        const parsedHotel = parseHotelInfo(travelData?.hotel_assigned);
        const parsedVisa = parseVisaInfo(travelData?.visa_letter_info);
        const normalizedTravel = {
          flight_issued: toYesNoOrEmpty(travelData?.flight_issued),
          flight_number: travelData?.flight_number || '',
          arrival_date: travelData?.arrival_date ? String(travelData.arrival_date).split('T')[0] : '',
          arrival_time: travelData?.arrival_time || '',
          departure_date: travelData?.departure_date ? String(travelData.departure_date).split('T')[0] : '',
          departure_time: travelData?.departure_time || '',
          ...parsedHotel,
          visa_letter_required: toYesNoOrEmpty(travelData?.visa_letter_required),
          visa_letter_issued: toYesNoOrEmpty(travelData?.visa_letter_issued),
          // Do not spread parsedVisa { full_name, passport_number } here — empty strings would
          // overwrite profile.full_name from /api/profile when merging into setProfile().
          visa_letter_info_full_name: parsedVisa.full_name,
          visa_letter_info_passport_number: parsedVisa.passport_number,
        };

        setProfile({
          ...profileData,
          ...normalizedTravel,
        });
        setAvatarTimestamp(Date.now());
        if (profileData.avatar_url) {
          setChangingAvatar(true);
        }
        setFormData({
          full_name: profileData.full_name || '',
          org: profileData.org || '',
          company_capital_size: profileData.company_capital_size || '',
          position: profileData.position || '',
          seniority_level: profileData.seniority_level || '',
          nationality: profileData.nationality || '',
          country: profileData.country || '',
          phone_country_code: parsedPhone.code,
          phone_number: parsedPhone.number,
          sector: profileData.sector || '',
        });
        setTravelFormData({
          flight_issued: normalizedTravel.flight_issued,
          flight_number: normalizedTravel.flight_number,
          arrival_date: normalizedTravel.arrival_date,
          arrival_time: normalizedTravel.arrival_time,
          departure_date: normalizedTravel.departure_date,
          departure_time: normalizedTravel.departure_time,
        });
        setHotelFormData({
          hotel_assigned: normalizedTravel.hotel_assigned,
          hotel_name: normalizedTravel.hotel_name,
          check_in: normalizedTravel.check_in,
          check_out: normalizedTravel.check_out,
        });
        setVisaFormData({
          visa_letter_required: normalizedTravel.visa_letter_required,
          visa_letter_issued: normalizedTravel.visa_letter_issued,
          full_name: parsedVisa.full_name || profileData.full_name || '',
          passport_number: parsedVisa.passport_number,
        });
      }
    } catch (err) {
      setError(t('dashboard.errorLoadProfile'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setError('');
    setSuccess('');

    if (name === 'phone_number') {
      const normalized = stripCountryCodeFromNumber(value, formData.phone_country_code);
      setFormData(prev => ({ ...prev, [name]: normalized }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSecurityChange = (e) => {
    setSecurityData({ ...securityData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleUpdateProfile = async (e, onSuccess) => {
    e.preventDefault();

    // Validation
    const trimmedName = formData.full_name.trim();
    const trimmedPosition = formData.position.trim();
    const trimmedOrg = formData.org.trim();
    const trimmedPhoneNumber = formData.phone_number.trim();
    const trimmedSector = formData.sector.trim();

    if (!trimmedName || trimmedName.length < 3 || trimmedName.length > 35) {
      setError(t('profileSetup.errorFullName'));
      return;
    }
    if (!trimmedPosition || trimmedPosition.length > 30) {
      setError(t('profileSetup.errorDesignation'));
      return;
    }
    if (!trimmedOrg || trimmedOrg.length > 40) {
      setError(t('profileSetup.errorCompany'));
      return;
    }
    if (!formData.nationality) {
      setError(t('profileSetup.errorNationality'));
      return;
    }
    if (!formData.country) {
      setError(t('profileSetup.errorCountry'));
      return;
    }
    if (!trimmedPhoneNumber || !/^\d{1,10}$/.test(trimmedPhoneNumber)) {
      setError(t('profileSetup.errorPhone'));
      return;
    }
    if (!trimmedSector || trimmedSector.length > 45) {
      setError(t('profileSetup.errorSector'));
      return;
    }
    if (!formData.company_capital_size) {
      setError(t('dashboard.profileFields.errorCapitalRequired'));
      return;
    }
    if (!formData.seniority_level) {
      setError(t('dashboard.profileFields.errorSeniorityRequired'));
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload = {
        ...formData,
        full_name: trimmedName,
        position: trimmedPosition,
        org: trimmedOrg,
        phone_number: trimmedPhoneNumber,
        phone: `${formData.phone_country_code}${trimmedPhoneNumber}`,
        sector: trimmedSector,
        company_capital_size: formData.company_capital_size,
        seniority_level: formData.seniority_level,
      };

      const updated = await publicApi.updateProfile(payload);
      setProfile(updated);
      setSuccess(t('dashboard.profileUpdatedSuccess'));
      if (refreshUser) await refreshUser();
      onSuccess?.();
    } catch (err) {
      setError(err.message || t('dashboard.errorUpdateProfile'));
    } finally {
      setSaving(false);
    }
  };

  const handleTravelSubmit = async (e) => {
    e.preventDefault();
    if (!travelFormData.flight_issued) {
      setError('Please provide if flight has been issued.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await publicApi.updateTravelDetails({
        flight_issued: travelFormData.flight_issued,
        flight_number: travelFormData.flight_issued === 'yes' ? travelFormData.flight_number : '',
        arrival_date: travelFormData.flight_issued === 'yes' ? travelFormData.arrival_date : '',
        arrival_time: travelFormData.flight_issued === 'yes' ? travelFormData.arrival_time : '',
        departure_date: travelFormData.flight_issued === 'yes' ? travelFormData.departure_date : '',
        departure_time: travelFormData.flight_issued === 'yes' ? travelFormData.departure_time : '',
      });
      await loadProfile();
      setSuccess(t('dashboard.travelSavedSuccess'));
      setOpenModal(null);
    } catch (err) {
      setError(err?.message || t('dashboard.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleHotelSubmit = async (e) => {
    e.preventDefault();
    if (!hotelFormData.hotel_assigned) {
      setError('Please provide if hotel is assigned.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await publicApi.updateTravelDetails({
        hotel_assigned: hotelFormData.hotel_assigned,
        hotel_name: hotelFormData.hotel_assigned === 'yes' ? hotelFormData.hotel_name : '',
        check_in: hotelFormData.hotel_assigned === 'yes' ? hotelFormData.check_in : '',
        check_out: hotelFormData.hotel_assigned === 'yes' ? hotelFormData.check_out : '',
      });
      await loadProfile();
      setSuccess(t('dashboard.hotelSavedSuccess'));
      setOpenModal(null);
    } catch (err) {
      setError(err?.message || t('dashboard.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleVisaSubmit = async (e) => {
    e.preventDefault();
    if (!visaFormData.visa_letter_required) {
      setError('Please provide if visa letter is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await publicApi.updateTravelDetails({
        visa_letter_required: visaFormData.visa_letter_required,
        visa_letter_issued:
          visaFormData.visa_letter_required === 'yes' ? visaFormData.visa_letter_issued : 'no',
        visa_letter_full_name:
          visaFormData.visa_letter_required === 'yes' &&
          visaFormData.visa_letter_issued === 'no'
            ? visaFormData.full_name
            : '',
        visa_letter_passport:
          visaFormData.visa_letter_required === 'yes' &&
          visaFormData.visa_letter_issued === 'no'
            ? visaFormData.passport_number
            : '',
      });
      await loadProfile();
      setSuccess(t('dashboard.visaSavedSuccess'));
      setOpenModal(null);
    } catch (err) {
      setError(err?.message || t('dashboard.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (securityData.newPassword.length < 6) {
      setError(t('dashboard.errorPasswordLength') || 'New password must be at least 6 characters long.');
      return;
    }
    if (securityData.newPassword !== securityData.confirmPassword) {
      setError(t('dashboard.errorPasswordMismatch') || 'New passwords do not match.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await publicApi.changePassword({
        oldPassword: securityData.oldPassword,
        newPassword: securityData.newPassword,
      });
      setSuccess(t('dashboard.passwordChangedSuccess'));
      setSecurityData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setOpenModal(null);
    } catch (err) {
      setError(err.message || t('dashboard.errorUpdateProfile'));
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarClick = () => {
    if (changingAvatar) return;
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Optional: Frontend size check if we want to be proactive
    // if (file.size > 10 * 1024 * 1024) { // 10MB limit check
    //   setError('Picture size too big. Please choose an image smaller than 10MB.');
    //   return;
    // }

    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        setChangingAvatar(true);
        setError('');
        setSuccess('');
        const base64String = reader.result;
        const updated = await publicApi.updateProfile({ avatar: base64String });
        setProfile(updated);
        setAvatarTimestamp(Date.now());
        setSuccess(t('dashboard.pictureUpdatedSuccess'));
        if (refreshUser) await refreshUser();
      } catch (err) {
        setChangingAvatar(false);
        if (err.message?.includes('413') || err.message?.toLowerCase().includes('large')) {
          setError(t('dashboard.errorPictureTooBig'));
        } else {
          setError(t('dashboard.errorPictureFailed'));
        }
        console.error('Avatar update error:', err);
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveAvatar = async (e) => {
    e.stopPropagation();
    if (!profile?.avatar_url) return;
    const confirm = window.confirm(t('dashboard.removePictureConfirm'));
    if (!confirm) return;

    try {
      setChangingAvatar(true);
      setError('');
      setSuccess('');
      const updated = await publicApi.updateProfile({ avatar: null });
      setProfile(updated);
      setAvatarTimestamp(Date.now());
      setSuccess(t('dashboard.pictureRemovedSuccess'));
      if (refreshUser) await refreshUser();
    } catch (err) {
      setError(t('dashboard.errorRemovePicture'));
    } finally {
      setChangingAvatar(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteUnderstand) return;
    try {
      setSaving(true);
      await publicApi.deleteAccount();
      setOpenModal(null);
      logout();
      navigate('/');
    } catch (err) {
      setError(t('dashboard.errorDeleteAccount'));
    } finally {
      setSaving(false);
    }
  };

  const displayId = profile?.public_id || profile?.qr || profile?.id;

  const handleCopyId = () => {
    const value = displayId;
    if (!value) return;

    const text = String(value);

    const markCopied = () => {
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 1500);
    };

    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(markCopied).catch(() => {
        try {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.focus();
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
          markCopied();
        } catch {
          // ignore copy errors
        }
      });
    } else {
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        markCopied();
      } catch {
        // ignore copy errors
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-[#8FD2C7]/10">
        <div className="flex flex-col items-center gap-4">
          <div className="h-14 w-14 rounded-2xl border-2 border-[#492E8B]/20 border-t-[#492E8B] animate-spin" />
          <p className="text-sm font-medium text-slate-500">{t('dashboard.loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full flex flex-col" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Full-page background */}
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-slate-50 via-white to-[#8FD2C7]/10" />
      <div className="fixed inset-0 -z-10 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(73,46,139,0.08),transparent)]" />

      {/* Hero – full-width gradient */}
      <motion.section
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }}
        className="relative overflow-hidden bg-gradient-to-br from-[#492E8B] via-[#5a3a9e] to-[#8FD2C7] rounded-b-[2rem] shadow-xl shadow-[#492E8B]/20"
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(143,210,199,0.25),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.1),transparent_40%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-10">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-10">
            {/* Avatar */}
            <div className="relative group cursor-pointer shrink-0" onClick={handleAvatarClick}>
              <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl overflow-hidden border-4 border-white/30 shadow-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center ring-4 ring-white/20">
                <AnimatePresence>
                  {changingAvatar && (
                    <motion.div key="loader" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-20 bg-[#492E8B]/80 flex flex-col items-center justify-center gap-2">
                      <div className="h-8 w-8 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      <span className="text-[10px] font-bold text-white uppercase tracking-wider">{t('dashboard.updatingAvatar')}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
                {profile?.avatar_url ? (
                  <motion.img
                    key={`${profile.avatar_url}-${avatarTimestamp}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onLoad={() => setChangingAvatar(false)}
                    onError={() => setChangingAvatar(false)}
                    src={`${publicApi.toAbsoluteUrl(profile.avatar_url)}?t=${avatarTimestamp}`}
                    alt={t('dashboard.profile.avatarAlt') || 'Profile'}
                    className={`w-full h-full object-cover ${changingAvatar ? 'opacity-0' : 'opacity-100'}`}
                  />
                ) : (
                  <FaUser className="text-4xl md:text-5xl text-white/70" />
                )}
              </div>
              {!changingAvatar && (
                <div className="absolute inset-0 rounded-2xl bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex gap-3">
                    <span className="p-2 rounded-xl bg-white/20 hover:bg-white/30"><FaCamera className="text-white text-lg" title={t('dashboard.profile.changePicture') || 'Change Picture'} /></span>
                    {profile?.avatar_url && (
                      <span className="p-2 rounded-xl bg-white/20 hover:bg-red-400/80" onClick={handleRemoveAvatar}><FaTrash className="text-white text-lg" title={t('dashboard.profile.removePicture') || 'Remove Picture'} /></span>
                    )}
                  </div>
                </div>
              )}
              <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />
            </div>

            <div className="flex-1 text-center md:text-left">
              {/* Role (and VIP) above the name */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mb-2">
                <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm text-white rounded-xl text-[11px] font-semibold uppercase tracking-wider">
                  {profile?.role || (t('dashboard.guest') || 'Guest')}
                </span>
                {profile?.vip && (
                  <span className="px-3 py-1.5 bg-amber-400/30 backdrop-blur-sm text-white rounded-xl text-[11px] font-semibold uppercase tracking-wider">
                    {t('dashboard.vip') || 'VIP'}
                  </span>
                )}
              </div>

              {/* Name */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white drop-shadow-sm">
                {profile?.full_name}
              </h1>

              {/* Email – aligned with name */}
              <p className="mt-1 flex items-center justify-center md:justify-start gap-2 text-white/90 text-sm md:text-base">
                <FaEnvelope className="text-[#8FD2C7] shrink-0" /> {profile?.email}
              </p>

              {/* ID – aligned with name & email */}
              {displayId && (
                <p className="mt-1 flex items-center justify-center md:justify-start gap-2 text-white/90 text-xs md:text-sm">
                  <FaIdCard className="w-3.5 h-3.5 text-[#8FD2C7] shrink-0" aria-hidden />
                  <span className="opacity-90 text-[11px] md:text-xs normal-case">
                    {t('dashboard.uniqueAttendeeId')}:
                  </span>
                  <span className="font-semibold tabular-nums tracking-wide">
                    {displayId}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyId}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/25 bg-white/10 text-[10px] md:text-[11px] font-medium hover:bg-white/20 hover:border-white/40 transition-colors"
                    aria-label="Copy ID"
                  >
                    <FaCopy className="w-3 h-3" />
                    <span>{copiedId ? 'Copied' : 'Copy'}</span>
                  </button>
                </p>
              )}
            </div>

            {/* QR – glass card */}
            <div className="shrink-0 bg-white/15 backdrop-blur-md rounded-2xl p-4 border border-white/20 shadow-xl">
              <div className="bg-white p-2 rounded-xl">
                <QRCode value={profile?.qr || profile?.id || 'No QR'} size={100} />
              </div>
              <p className="text-[10px] uppercase font-bold text-white/80 tracking-widest mt-2">{t('dashboard.profileEntryPass')}</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Scrollable content */}
      <div className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16">
        {(error || success) && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className={`mb-6 p-4 rounded-2xl border ${error ? 'bg-red-50 border-red-200 text-red-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}
          >
            {error || success}
          </motion.div>
        )}

        {/* Row 1: Travel, Hotel, Visa */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.06 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden hover:shadow-xl hover:shadow-sky-100/50 transition-all duration-300">
            <div className="p-4 border-b border-slate-100 bg-gradient-to-br from-sky-50 to-white flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 text-white shadow-lg shadow-sky-200">
                <FaPlane className="text-lg" />
              </div>
              <h3 className="font-bold text-slate-900">{t('dashboard.profileTravelDetails')}</h3>
            </div>
            <div className="p-4 space-y-2.5 text-sm">
              <DetailRow label={t('dashboard.travelDetails.flightIssued')} value={profile?.flight_issued ? (profile.flight_issued === 'yes' ? t('dashboard.travelDetails.yes') : t('dashboard.travelDetails.no')) : t('dashboard.notProvided')} />
              {profile?.flight_issued === 'yes' && (
                <>
                  <DetailRow label={t('dashboard.travelDetails.flightNumber')} value={profile?.flight_number || '—'} />
                  <DetailRow label={t('dashboard.travelDetails.arrivalDate')} value={profile?.arrival_date || '—'} />
                  <DetailRow label={t('dashboard.travelDetails.departureDate')} value={profile?.departure_date || '—'} />
                </>
              )}
              <button type="button" onClick={() => { setError(''); setOpenModal('travel'); }} className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-600 hover:text-sky-700 mt-2">
                <FaPencilAlt className="w-3 h-3" /> {t('dashboard.edit')}
              </button>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden hover:shadow-xl hover:shadow-emerald-100/50 transition-all duration-300">
            <div className="p-4 border-b border-slate-100 bg-gradient-to-br from-emerald-50 to-white flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-200">
                <FaHotel className="text-lg" />
              </div>
              <h3 className="font-bold text-slate-900">{t('dashboard.profileHotel')}</h3>
            </div>
            <div className="p-4 space-y-2.5 text-sm">
              <DetailRow label={t('dashboard.hotel.hotelAssigned')} value={profile?.hotel_assigned ? (profile.hotel_assigned === 'yes' ? t('dashboard.travelDetails.yes') : t('dashboard.travelDetails.no')) : t('dashboard.notProvided')} />
              {profile?.hotel_assigned === 'yes' && (
                <>
                  <DetailRow label={t('dashboard.hotel.hotelName')} value={profile?.hotel_name || '—'} />
                  <DetailRow label={t('dashboard.hotel.checkIn')} value={profile?.check_in || '—'} />
                  <DetailRow label={t('dashboard.hotel.checkOut')} value={profile?.check_out || '—'} />
                </>
              )}
              <button type="button" onClick={() => { setError(''); setOpenModal('hotel'); }} className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 hover:text-emerald-700 mt-2">
                <FaPencilAlt className="w-3 h-3" /> {t('dashboard.edit')}
              </button>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden hover:shadow-xl hover:shadow-amber-100/50 transition-all duration-300">
            <div className="p-4 border-b border-slate-100 bg-gradient-to-br from-amber-50 to-white flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-200">
                <FaFileSignature className="text-lg" />
              </div>
              <h3 className="font-bold text-slate-900">{t('dashboard.profileVisaLetter')}</h3>
            </div>
            <div className="p-4 space-y-2.5 text-sm">
              <DetailRow label={t('dashboard.visaLetter.visaLetterRequired')} value={profile?.visa_letter_required ? (profile.visa_letter_required === 'yes' ? t('dashboard.travelDetails.yes') : t('dashboard.travelDetails.no')) : t('dashboard.notProvided')} />
              {profile?.visa_letter_required === 'yes' && (
                <DetailRow label={t('dashboard.visaLetter.visaLetterIssued')} value={profile?.visa_letter_issued ? (profile.visa_letter_issued === 'yes' ? t('dashboard.travelDetails.yes') : t('dashboard.travelDetails.no')) : '—'} />
              )}
              <button type="button" onClick={() => { setError(''); setOpenModal('visa'); }} className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-600 hover:text-amber-700 mt-2">
                <FaPencilAlt className="w-3 h-3" /> {t('dashboard.edit')}
              </button>
            </div>
          </div>
        </motion.section>

        {/* Row 2: Personal (2/3) + Driver (1/3) */}
        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden hover:shadow-xl transition-all duration-300">
            <div className="p-4 border-b border-slate-100 bg-gradient-to-br from-indigo-50 to-white flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-lg shadow-indigo-200">
                  <FaIdCard className="text-lg" />
                </div>
                <h3 className="font-bold text-slate-900">{t('dashboard.profilePersonalInfo')}</h3>
              </div>
              <button type="button" onClick={() => { setError(''); setOpenModal('personal'); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-700 text-white text-sm font-semibold hover:from-indigo-700 hover:to-indigo-800 shadow-lg shadow-indigo-200/50 transition-all">
                <FaPencilAlt className="w-3.5 h-3.5" /> {t('dashboard.edit')}
              </button>
            </div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
              <DetailRow label={t('dashboard.fullName')} value={profile?.full_name} />
              <DetailRow label={t('dashboard.organization')} value={profile?.org} />
              <DetailRow
                label={t('dashboard.profileFields.companyCapital')}
                value={
                  (() => {
                    const opt = PROFILE_COMPANY_CAPITAL_OPTIONS.find((o) => o.value === profile?.company_capital_size);
                    return opt ? t(opt.labelKey) : t('dashboard.notProvided');
                  })()
                }
              />
              <DetailRow label={t('dashboard.position')} value={profile?.position} />
              <DetailRow
                label={t('dashboard.profileFields.seniorityLevel')}
                value={
                  (() => {
                    const opt = PROFILE_SENIORITY_OPTIONS.find((o) => o.value === profile?.seniority_level);
                    return opt ? t(opt.labelKey) : t('dashboard.notProvided');
                  })()
                }
              />
              <DetailRow label={t('dashboard.formLabelPhone').replace(' *', '')} value={profile?.phone} />
              <DetailRow label={t('dashboard.formLabelNationality').replace(' *', '')} value={profile?.nationality} />
              <DetailRow label={t('dashboard.formLabelCountry').replace(' *', '')} value={profile?.country} />
              <DetailRow label={t('dashboard.formLabelSector').replace(' *', '')} value={profile?.sector} className="sm:col-span-2" />
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden hover:shadow-xl transition-all duration-300">
            <div className="p-4 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white flex items-center gap-3">
              <div className="p-3 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 text-white shadow-lg shadow-slate-200">
                <FaCar className="text-lg" />
              </div>
              <h3 className="font-bold text-slate-900">{t('dashboard.assignedDriver')}</h3>
            </div>
            <div className="p-4 space-y-2.5 text-sm">
              <DetailRow label={t('dashboard.driverName')} value={profile?.driver_name} />
              <DetailRow label={t('dashboard.driverContact')} value={profile?.driver_contact} />
              {(!profile?.driver_name && !profile?.driver_contact) && (
                <p className="text-slate-500 text-xs italic pt-1">{t('dashboard.notProvided')}</p>
              )}
            </div>
          </div>
        </motion.section>

        {/* Row 3: Security + Danger */}
        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.14 }} className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden hover:shadow-xl transition-all duration-300">
            <div className="p-4 border-b border-slate-100 bg-gradient-to-br from-amber-50 to-white flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg shadow-amber-200">
                  <FaShieldAlt className="text-lg" />
                </div>
                <h3 className="font-bold text-slate-900">{t('dashboard.profileSecurity')}</h3>
              </div>
              <button type="button" onClick={() => { setError(''); setOpenModal('password'); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 text-white text-sm font-semibold hover:bg-slate-900 shadow-lg transition-all">
                {t('dashboard.changePassword')}
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-slate-600">{t('dashboard.securityDescription')}</p>
            </div>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-50/80 rounded-2xl shadow-lg shadow-red-100/50 border border-red-100 overflow-hidden hover:shadow-xl hover:shadow-red-200/50 transition-all duration-300">
            <div className="p-4 border-b border-red-100 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-red-100 text-red-600">
                  <FaTrash className="text-lg" />
                </div>
                <h3 className="font-bold text-red-900">{t('dashboard.profileDangerZone')}</h3>
              </div>
              <button type="button" onClick={() => { setError(''); setDeleteUnderstand(false); setOpenModal('delete'); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-red-600 border-2 border-red-200 text-sm font-semibold hover:bg-red-600 hover:text-white hover:border-red-600 transition-all shadow-sm">
                {t('dashboard.deleteAccount')}
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-red-700">{t('dashboard.dangerDescription')}</p>
            </div>
          </div>
        </motion.section>
      </div>

      {/* Modals */}
      <ProfileModal isOpen={openModal === 'personal'} onClose={() => setOpenModal(null)} title={t('dashboard.profilePersonalInfo')}>
        <form onSubmit={(e) => handleUpdateProfile(e, () => setOpenModal(null))} className="space-y-4">
          {error && <div className="p-3 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm" role="alert">{error}</div>}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{t('dashboard.formLabelFullName')}</label>
              <input type="text" name="full_name" value={formData.full_name} onChange={handleFormChange} maxLength={35} required className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#492E8B]/25 focus:border-[#492E8B] outline-none transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{t('dashboard.formLabelOrganization')}</label>
              <input type="text" name="org" value={formData.org} onChange={handleFormChange} maxLength={40} required className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#492E8B]/25 focus:border-[#492E8B] outline-none transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{t('dashboard.profileFields.companyCapital')}</label>
              <select
                name="company_capital_size"
                value={formData.company_capital_size}
                onChange={handleFormChange}
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#492E8B]/25 focus:border-[#492E8B] outline-none transition-colors appearance-none bg-white"
              >
                <option value="">{t('dashboard.profileFields.selectPlaceholder')}</option>
                {PROFILE_COMPANY_CAPITAL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{t('dashboard.formLabelPosition')}</label>
              <input type="text" name="position" value={formData.position} onChange={handleFormChange} maxLength={30} required className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#492E8B]/25 focus:border-[#492E8B] outline-none transition-colors" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{t('dashboard.profileFields.seniorityLevel')}</label>
              <select
                name="seniority_level"
                value={formData.seniority_level}
                onChange={handleFormChange}
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#492E8B]/25 focus:border-[#492E8B] outline-none transition-colors appearance-none bg-white"
              >
                <option value="">{t('dashboard.profileFields.selectPlaceholder')}</option>
                {PROFILE_SENIORITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {t(o.labelKey)}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{t('dashboard.formLabelCode')}</label>
                <select name="phone_country_code" value={formData.phone_country_code} onChange={handleFormChange} className="w-full px-3 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-[#492E8B]/25 focus:border-[#492E8B] outline-none transition-colors">
                  {COUNTRY_CODE_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{t('dashboard.formLabelPhone')}</label>
                <input type="text" name="phone_number" value={formData.phone_number} onChange={handleFormChange} maxLength={10} required className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#492E8B]/25 focus:border-[#492E8B] outline-none transition-colors" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{t('dashboard.formLabelNationality')}</label>
              <select name="nationality" value={formData.nationality} onChange={handleFormChange} required className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#492E8B]/25 focus:border-[#492E8B] outline-none transition-colors appearance-none bg-white">
                <option value="">{t('dashboard.select')}</option>
                {NATIONALITY_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{t('dashboard.formLabelCountry')}</label>
              <select name="country" value={formData.country} onChange={handleFormChange} required className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#492E8B]/25 focus:border-[#492E8B] outline-none transition-colors appearance-none bg-white">
                <option value="">{t('dashboard.select')}</option>
                {COUNTRY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{t('dashboard.formLabelSector')}</label>
              <input type="text" name="sector" value={formData.sector} onChange={handleFormChange} maxLength={45} required className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#492E8B]/25 focus:border-[#492E8B] outline-none transition-colors" />
            </div>
          </div>
          <button type="submit" disabled={saving} className="w-full py-3.5 bg-gradient-to-r from-[#492E8B] to-[#5a3a9e] text-white rounded-xl font-semibold hover:opacity-95 disabled:opacity-50 shadow-lg shadow-[#492E8B]/20 transition-opacity">
            {saving ? t('dashboard.saving') : t('dashboard.save')}
          </button>
        </form>
      </ProfileModal>

      <ProfileModal isOpen={openModal === 'travel'} onClose={() => setOpenModal(null)} title={t('dashboard.profileTravelDetails')}>
        <form onSubmit={handleTravelSubmit} className="space-y-4">
          {error && <div className="p-3 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.travelDetails.flightIssued')}</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setTravelFormData((p) => ({ ...p, flight_issued: 'yes' }))} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${travelFormData.flight_issued === 'yes' ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{t('dashboard.travelDetails.yes')}</button>
              <button type="button" onClick={() => setTravelFormData((p) => ({ ...p, flight_issued: 'no' }))} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${travelFormData.flight_issued === 'no' ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{t('dashboard.travelDetails.no')}</button>
            </div>
          </div>
          {travelFormData.flight_issued === 'yes' && (
            <>
              <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('dashboard.travelDetails.flightNumber')}</label><input type="text" value={travelFormData.flight_number} onChange={(e) => setTravelFormData((p) => ({ ...p, flight_number: e.target.value }))} placeholder={t('dashboard.placeholders.flightNumber') || 'e.g. WY 101'} className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-sky-500/20" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('dashboard.travelDetails.arrivalDate')}</label><input type="date" value={travelFormData.arrival_date} onChange={(e) => setTravelFormData((p) => ({ ...p, arrival_date: e.target.value }))} className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-sky-500/20" /></div>
                <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('dashboard.travelDetails.arrivalTime')}</label><input type="time" value={travelFormData.arrival_time} onChange={(e) => setTravelFormData((p) => ({ ...p, arrival_time: e.target.value }))} className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-sky-500/20" /></div>
                <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('dashboard.travelDetails.departureDate')}</label><input type="date" value={travelFormData.departure_date} onChange={(e) => setTravelFormData((p) => ({ ...p, departure_date: e.target.value }))} className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-sky-500/20" /></div>
                <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('dashboard.travelDetails.departureTime')}</label><input type="time" value={travelFormData.departure_time} onChange={(e) => setTravelFormData((p) => ({ ...p, departure_time: e.target.value }))} className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-sky-500/20" /></div>
              </div>
            </>
          )}
          <button type="submit" disabled={saving} className="w-full py-3 bg-sky-600 text-white rounded-xl font-bold hover:bg-sky-700 disabled:opacity-50">{saving ? t('dashboard.saving') : t('dashboard.save')}</button>
        </form>
      </ProfileModal>

      <ProfileModal isOpen={openModal === 'hotel'} onClose={() => setOpenModal(null)} title={t('dashboard.profileHotel')}>
        <form onSubmit={handleHotelSubmit} className="space-y-4">
          {error && <div className="p-3 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.hotel.hotelAssigned')}</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setHotelFormData((p) => ({ ...p, hotel_assigned: 'yes' }))} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${hotelFormData.hotel_assigned === 'yes' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{t('dashboard.travelDetails.yes')}</button>
              <button type="button" onClick={() => setHotelFormData((p) => ({ ...p, hotel_assigned: 'no' }))} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${hotelFormData.hotel_assigned === 'no' ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{t('dashboard.travelDetails.no')}</button>
            </div>
          </div>
          {hotelFormData.hotel_assigned === 'yes' && (
            <>
              <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('dashboard.hotel.hotelName')}</label><input type="text" value={hotelFormData.hotel_name} onChange={(e) => setHotelFormData((p) => ({ ...p, hotel_name: e.target.value }))} placeholder={t('dashboard.placeholders.hotelName') || 'e.g. Al Bustan Palace'} className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500/20" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('dashboard.hotel.checkIn')}</label><input type="date" value={hotelFormData.check_in} onChange={(e) => setHotelFormData((p) => ({ ...p, check_in: e.target.value }))} className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500/20" /></div>
                <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('dashboard.hotel.checkOut')}</label><input type="date" value={hotelFormData.check_out} onChange={(e) => setHotelFormData((p) => ({ ...p, check_out: e.target.value }))} className="w-full px-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-emerald-500/20" /></div>
              </div>
            </>
          )}
          <button type="submit" disabled={saving} className="w-full py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 disabled:opacity-50">{saving ? t('dashboard.saving') : t('dashboard.save')}</button>
        </form>
      </ProfileModal>

      <ProfileModal isOpen={openModal === 'visa'} onClose={() => setOpenModal(null)} title={t('dashboard.profileVisaLetter')}>
        <form onSubmit={handleVisaSubmit} className="space-y-4">
          {error && <div className="p-3 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>}
          <p className="text-xs text-amber-700 bg-amber-50 rounded-xl p-3">{t('dashboard.visaLetter.note')}</p>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.visaLetter.visaLetterRequired')}</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setVisaFormData((p) => ({ ...p, visa_letter_required: 'yes' }))} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${visaFormData.visa_letter_required === 'yes' ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{t('dashboard.travelDetails.yes')}</button>
              <button type="button" onClick={() => setVisaFormData((p) => ({ ...p, visa_letter_required: 'no' }))} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${visaFormData.visa_letter_required === 'no' ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{t('dashboard.travelDetails.no')}</button>
            </div>
          </div>
          {visaFormData.visa_letter_required === 'yes' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('dashboard.visaLetter.visaLetterIssued')}</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setVisaFormData((p) => ({ ...p, visa_letter_issued: 'yes' }))} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${visaFormData.visa_letter_issued === 'yes' ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{t('dashboard.travelDetails.yes')}</button>
                <button type="button" onClick={() => setVisaFormData((p) => ({ ...p, visa_letter_issued: 'no' }))} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${visaFormData.visa_letter_issued === 'no' ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-600'}`}>{t('dashboard.travelDetails.no')}</button>
              </div>
            </div>
          )}
          <button type="submit" disabled={saving} className="w-full py-3 bg-amber-600 text-white rounded-xl font-bold hover:bg-amber-700 disabled:opacity-50">{saving ? t('dashboard.saving') : t('dashboard.save')}</button>
        </form>
      </ProfileModal>

      <ProfileModal isOpen={openModal === 'password'} onClose={() => setOpenModal(null)} title={t('dashboard.changePassword')}>
        <form onSubmit={handleChangePassword} className="space-y-4">
          {error && <div className="p-3 rounded-xl bg-red-50 text-red-700 text-sm">{error}</div>}
          <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('dashboard.currentPassword')}</label><input type="password" name="oldPassword" value={securityData.oldPassword} onChange={handleSecurityChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 outline-none" /></div>
          <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('dashboard.newPassword')}</label><input type="password" name="newPassword" value={securityData.newPassword} onChange={handleSecurityChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 outline-none" /></div>
          <div><label className="block text-xs font-bold text-slate-500 uppercase mb-1">{t('dashboard.confirmNewPassword')}</label><input type="password" name="confirmPassword" value={securityData.confirmPassword} onChange={handleSecurityChange} className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 outline-none" /></div>
          <button type="submit" disabled={saving} className="w-full py-3 bg-slate-800 text-white rounded-xl font-bold hover:bg-slate-900 disabled:opacity-50">{saving ? t('dashboard.saving') : t('dashboard.updatePassword')}</button>
        </form>
      </ProfileModal>

      <ProfileModal isOpen={openModal === 'delete'} onClose={() => setOpenModal(null)} title={t('dashboard.deleteAccount')}>
        <div className="space-y-4">
          <p className="text-slate-600">{t('dashboard.deleteAccountConfirm')}</p>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={deleteUnderstand} onChange={(e) => setDeleteUnderstand(e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500" />
            <span className="text-sm text-slate-700">{t('dashboard.deleteAccountUnderstand')}</span>
          </label>
          <button type="button" onClick={handleDeleteAccount} disabled={!deleteUnderstand || saving} className="w-full py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? t('dashboard.saving') : t('dashboard.deleteAccount')}
          </button>
        </div>
      </ProfileModal>
    </div>
  );
};

const Profile = () => (
  <DashboardLayout>
    <ProfileContent />
  </DashboardLayout>
);

export default Profile;
